import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { formatCurrency } from "utils/helpers/accounting";
import { ROUTES } from "utils";
import { useNavigate } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const todayString = formatDate(today);

type GTGTQuery = {
  from_date: string;
  to_date: string;
};

const RpDownGTGT: React.FC = () => {
  const navigate = useNavigate();
  const { fetchRpDownGtgt } = useReport();
  const [filters, setFilters] = useState<GTGTQuery>({
    from_date: firstOfMonth,
    to_date: todayString,
  });
  const [submitted, setSubmitted] = useState<GTGTQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof GTGTQuery, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) return;
      setSubmitted(filters);
      fetchRpDownGtgt.mutate({ from_date: filters.from_date, to_date: filters.to_date });
    },
    [fetchRpDownGtgt, filters],
  );

  const handleClearDate = (key: keyof GTGTQuery) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
    setSubmitted(null);
  };

  const records = (fetchRpDownGtgt.data?.data?.records ?? fetchRpDownGtgt.data?.data ?? []) as any[];
  const summary = (fetchRpDownGtgt.data?.data?.summary ?? null) as any;
  const isLoading = fetchRpDownGtgt.isPending;

  const columns = useMemo(
    () => [
      { headerName: "Số chứng từ", field: "voucher_code", pinned: "left", minWidth: 130 },
      { headerName: "Ngày CT", field: "accounting_date", minWidth: 110, valueFormatter: (p: any) => p.value ? new Date(p.value).toLocaleDateString("vi-VN") : "" },
      { headerName: "Số hóa đơn", field: "invoice_number", minWidth: 120 },
      { headerName: "Ngày HĐ", field: "invoice_date", minWidth: 100, valueFormatter: (p: any) => p.value ? new Date(p.value).toLocaleDateString("vi-VN") : "" },
      { headerName: "Tên KH người mua", field: "buyer_name", minWidth: 200, flex: 1 },
      { headerName: "MST người mua", field: "buyer_tax_code", minWidth: 130 },
      { headerName: "Mã hàng", field: "product_code", minWidth: 110 },
      { headerName: "Tên hàng hóa, dịch vụ", field: "product_name", minWidth: 200 },
      { headerName: "SL", field: "quantity", minWidth: 80, cellClass: "text-right" },
      { headerName: "Đơn giá", field: "unit_price", minWidth: 110, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right" },
      { headerName: "Thành tiền", field: "amount", minWidth: 130, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right" },
      { headerName: "Thuế suất GTGT (%)", field: "original_vat_rate", minWidth: 130, cellClass: "text-right" },
      { headerName: "Tiền thuế GTGT", field: "original_vat_amount", minWidth: 140, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right" },
      { headerName: "Tỷ lệ giảm (%)", field: "vat_reduction_rate", minWidth: 120, cellClass: "text-right text-orange-600" },
      { headerName: "Số tiền giảm", field: "vat_reduction_amount", minWidth: 130, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right text-orange-600 font-medium" },
      { headerName: "Thuế GTGT phải nộp", field: "vat_payable", minWidth: 150, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right font-semibold text-green-700" },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: "agTextColumnFilter", floatingFilter: true, minWidth: 100 }),
    [],
  );

  return (
    <div className="p-6 space-y-5">
      {isLoading && <LoadingState message="Đang tải báo cáo" description="Vui lòng chờ trong giây lát." />}

      <nav className="text-sm text-blue-600 flex items-center gap-1 flex-wrap">
        <span className="cursor-pointer hover:underline font-medium" onClick={() => navigate(ROUTES.PRIVATE_ROOT.DASHBOARD)}>BÁO CÁO</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500 cursor-pointer hover:underline">Sổ sách, báo cáo quản trị</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 font-medium">Báo cáo chi tiết giảm GTGT</span>
      </nav>

      <h1 className="text-xl font-bold text-gray-900">Báo cáo chi tiết giảm thuế GTGT</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày <span className="text-red-500">(*)</span>
            </label>
            <div className="flex items-center gap-1">
              <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange("from_date", e.target.value)}
                className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              {filters.from_date && (
                <button type="button" onClick={() => handleClearDate("from_date")}
                  className="text-gray-400 hover:text-gray-600 text-lg leading-none font-medium">×</button>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày <span className="text-red-500">(*)</span>
            </label>
            <div className="flex items-center gap-1">
              <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange("to_date", e.target.value)}
                className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              {filters.to_date && (
                <button type="button" onClick={() => handleClearDate("to_date")}
                  className="text-gray-400 hover:text-gray-600 text-lg leading-none font-medium">×</button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button type="submit" disabled={isLoading}
              className="rounded border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
              Xem báo cáo
            </button>
            <button type="button"
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
              Xuất file Excel
            </button>
          </div>
        </div>
      </form>

      {/* Summary cards */}
      {submitted && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Tổng doanh thu", value: summary.total_amount, color: "text-gray-700" },
            { label: "Tổng thuế GTGT", value: summary.total_vat_amount, color: "text-blue-700" },
            { label: "Tổng tiền giảm", value: summary.total_reduction_amount, color: "text-orange-600" },
            { label: "Tổng GTGT phải nộp", value: summary.total_vat_payable, color: "text-green-700" },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
              <p className="text-xs text-gray-500">{card.label}</p>
              <p className={`text-base font-bold ${card.color}`}>{formatCurrency(card.value ?? 0)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {submitted ? (
          <>
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">
                {records.length} dòng · {submitted.from_date} → {submitted.to_date}
              </h2>
            </div>
            {records.length > 0
              ? <AgGridTable rows={records} columns={columns as any} defaultColDef={defaultColDef} pagination={true} autoHeight />
              : (
                <div className="p-10 text-center text-gray-400">
                  <p className="text-sm">Không có dữ liệu giảm GTGT trong khoảng thời gian đã chọn</p>
                </div>
              )
            }
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">Nhấn <strong>Xem báo cáo</strong> để tải dữ liệu</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RpDownGTGT;
